// src/routes/dashboard/admin/integrations.tsx
//
// Super-admin supervision screen for HRIS/payroll integrations (DESIGN_HRISIntegration.md §8.4).
// Two tabs: the provider registry (CRUD) and cross-org connection oversight (override).

import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { BRAND_MINT } from '@/lib/utils'
import {
  listAllProvidersApi,
  createProviderApi,
  listAllConnectionsApi,
  overrideConnectionApi,
  type IntegrationProvider,
  type IntegrationConnection,
  type IntegrationFieldDef,
  type ConnectionStatus,
} from '@/lib/integrations'
import {
  LoaderIcon,
  PlusIcon,
  PlugIcon,
  BuildingIcon,
  FlagIcon,
} from 'lucide-react'

type Tab = 'providers' | 'connections'

const CONNECTION_STATUSES: ConnectionStatus[] = ['configured', 'active', 'paused', 'disconnected']

function statusClass(status: string | null | undefined): string {
  switch (status) {
    case 'active':
    case 'success':
      return 'bg-emerald-500/10 text-emerald-600'
    case 'error':
    case 'failed':
      return 'bg-red-500/10 text-red-600'
    case 'partial':
      return 'bg-amber-500/10 text-amber-600'
    case 'paused':
    case 'disconnected':
      return 'bg-slate-500/10 text-slate-500'
    default:
      return 'bg-muted text-muted-foreground'
  }
}

export const Route = createFileRoute('/dashboard/admin/integrations')({
  component: () => {
    const [providers, setProviders] = useState<IntegrationProvider[]>([])
    const [connections, setConnections] = useState<IntegrationConnection[]>([])
    const [loading, setLoading] = useState(true)
    const [tab, setTab] = useState<Tab>('providers')

    // Create-provider dialog state
    const [showCreate, setShowCreate] = useState(false)
    const [newCode, setNewCode] = useState('')
    const [newName, setNewName] = useState('')
    const [newCategory, setNewCategory] = useState('hris')
    const [newAdapter, setNewAdapter] = useState('src/integrations/adapters/')
    const [newSchema, setNewSchema] = useState('[]')
    const [creating, setCreating] = useState(false)

    const load = async () => {
      try {
        const [p, c] = await Promise.all([listAllProvidersApi(), listAllConnectionsApi()])
        setProviders(p.providers)
        setConnections(c.connections)
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to load integrations')
      } finally {
        setLoading(false)
      }
    }

    useEffect(() => { load() }, [])

    const handleCreate = async () => {
      if (!newCode.trim() || !newName.trim()) { toast.error('Provider code and display name are required'); return }
      let schema: IntegrationFieldDef[]
      try {
        schema = JSON.parse(newSchema)
        if (!Array.isArray(schema)) throw new Error('schema must be an array')
      } catch {
        toast.error('Connection config schema must be a valid JSON array')
        return
      }

      setCreating(true)
      try {
        await createProviderApi({
          provider_code: newCode.trim(),
          display_name: newName.trim(),
          category: newCategory as IntegrationProvider['category'],
          adapter_module: newAdapter.trim(),
          connection_config_schema: schema,
        })
        toast.success(`Provider "${newName.trim()}" registered.`)
        setShowCreate(false)
        setNewCode(''); setNewName(''); setNewAdapter('src/integrations/adapters/'); setNewSchema('[]')
        await load()
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to register provider')
      } finally {
        setCreating(false)
      }
    }

    const handleOverride = async (connectionId: number, status: ConnectionStatus) => {
      try {
        await overrideConnectionApi(connectionId, { status })
        toast.success('Connection updated.')
        await load()
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to update connection')
      }
    }

    if (loading) {
      return (
        <div className="mx-auto max-w-5xl">
          <Header />
          <div className="flex items-center justify-center rounded-xl border border-border bg-card py-16">
            <LoaderIcon className="size-5 animate-spin text-muted-foreground" />
            <p className="ml-2 text-sm text-muted-foreground">Loading integrations…</p>
          </div>
        </div>
      )
    }

    return (
      <div className="mx-auto max-w-5xl">
        <Header onCreate={() => setShowCreate(true)} />

        <div className="mb-6 flex gap-1 rounded-lg bg-muted p-1 w-fit">
          <button onClick={() => setTab('providers')}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${tab === 'providers' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
            <span className="inline-flex items-center gap-1.5"><FlagIcon className="size-3.5" /> Providers ({providers.length})</span>
          </button>
          <button onClick={() => setTab('connections')}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${tab === 'connections' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
            <span className="inline-flex items-center gap-1.5"><BuildingIcon className="size-3.5" /> Connections ({connections.length})</span>
          </button>
        </div>

        {tab === 'providers' ? (
          providers.length === 0 ? (
            <EmptyState icon={FlagIcon} title="No providers registered" hint="Register a provider to let orgs connect to it." />
          ) : (
            <div className="space-y-3">
              {providers.map((p) => (
                <div key={p.provider_id} className="rounded-xl border border-border bg-card p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <FlagIcon className="size-4 shrink-0 text-primary" />
                        <span className="font-semibold">{p.display_name}</span>
                        <code className="rounded bg-muted px-2 py-0.5 text-xs font-semibold font-mono">{p.provider_code}</code>
                        <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">{p.category}</span>
                        {!p.is_active && <span className="rounded-full bg-slate-500/10 px-2 py-0.5 text-[10px] font-medium text-slate-500">inactive</span>}
                      </div>
                      <p className="mt-1 truncate text-xs font-mono text-muted-foreground">{p.adapter_module}</p>
                      <div className="mt-2 flex flex-wrap gap-1.5 text-[10px] text-muted-foreground">
                        {p.supports_employee_sync && <Chip>employees</Chip>}
                        {p.supports_payroll_sync && <Chip>payroll</Chip>}
                        {p.supports_writeback && <Chip>write-back</Chip>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          connections.length === 0 ? (
            <EmptyState icon={BuildingIcon} title="No connections yet" hint="Once orgs connect a provider, every connection shows here for oversight." />
          ) : (
            <div className="space-y-3">
              {connections.map((conn) => (
                <div key={conn.connection_id} className="rounded-xl border border-border bg-card p-5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <PlugIcon className="size-4 shrink-0 text-primary" />
                        <span className="font-semibold">{conn.org_name ?? `Org #${conn.org_id}`}</span>
                        <span className="text-xs text-muted-foreground">→ {conn.provider_name ?? conn.provider_code}</span>
                      </div>
                      <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${statusClass(conn.status)}`}>{conn.status}</span>
                        {conn.last_sync_status && (
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${statusClass(conn.last_sync_status)}`}>
                            sync: {conn.last_sync_status}
                          </span>
                        )}
                        {conn.last_sync_at && <span>{new Date(conn.last_sync_at).toLocaleString()}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        value={conn.status}
                        onChange={(e) => handleOverride(conn.connection_id, e.target.value as ConnectionStatus)}
                        className="rounded-lg border border-border bg-background px-2 py-1.5 text-sm outline-none focus:border-primary"
                      >
                        {CONNECTION_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {/* ── Register provider dialog ── */}
        <Dialog
          open={showCreate}
          onClose={() => setShowCreate(false)}
          title="Register integration provider"
          description="Add a provider so organizations can connect to it."
          footer={
            <>
              <Button variant="outline" size="sm" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button size="sm" onClick={handleCreate} disabled={creating} className="gap-1.5">
                {creating ? <LoaderIcon className="size-4 animate-spin" /> : <PlusIcon className="size-4" />}
                Register
              </Button>
            </>
          }
        >
          <div className="space-y-4 py-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Provider code *</label>
              <input type="text" value={newCode} onChange={(e) => setNewCode(e.target.value)}
                placeholder="e.g. zoho_people" autoFocus
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-mono outline-none focus:border-primary" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Display name *</label>
              <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Zoho People"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Category</label>
              <select value={newCategory} onChange={(e) => setNewCategory(e.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary">
                <option value="hris">HRIS</option>
                <option value="payroll">Payroll</option>
                <option value="hris_payroll">HRIS + Payroll</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Adapter module *</label>
              <input type="text" value={newAdapter} onChange={(e) => setNewAdapter(e.target.value)}
                placeholder="src/integrations/adapters/zohoPeople.js"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-mono outline-none focus:border-primary" />
              <p className="mt-1 text-[10px] text-muted-foreground/60">
                Built-in: zohoPeople.js · keka.js · sapSuccessFactors.js · genericSftp.js
              </p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Connection config schema (JSON array)</label>
              <textarea value={newSchema} onChange={(e) => setNewSchema(e.target.value)} rows={5}
                placeholder='[{"name":"api_key","type":"string","required":true,"secret":true}]'
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm font-mono outline-none focus:border-primary" />
            </div>
          </div>
        </Dialog>
      </div>
    )
  },
})

function Chip({ children }: { children: ReactNode }) {
  return <span className="rounded-full bg-primary/10 px-2 py-0.5 font-medium text-primary">{children}</span>
}

function EmptyState({ icon: Icon, title, hint }: { icon: typeof FlagIcon; title: string; hint: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border bg-card py-16">
      <Icon className="size-10 text-muted-foreground/40" />
      <p className="text-sm font-medium text-foreground">{title}</p>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  )
}

function Header({ onCreate }: { onCreate?: () => void }) {
  return (
    <div className="mb-8">
      <div className="flex items-start gap-3">
        <span aria-hidden="true" className="mt-0.5 shrink-0" style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }} />
        <div className="flex flex-1 items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Integrations</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              HRIS/payroll provider registry and cross-org connection oversight.
            </p>
          </div>
          {onCreate && (
            <Button size="sm" onClick={onCreate} className="gap-1.5 shrink-0">
              <PlusIcon className="size-4" />
              Register provider
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}